/* helper.ts */

/* React */
import type { RefObject } from "react";

/* d3.js */
import * as d3 from "d3";

/* Utils */
import {
  getCohortGroupColor,
  getStackSegmentColor,
  getStackSegmentColorKey,
  getStackGradientTargets,
} from "src/utils/helper/charts/colorMapper";
import { capitalizeFirstLetter } from "src/utils/helper/string-transformation";

import {
  createChartTooltip,
  ScssProps,
} from "src/utils/helper/chart-tooltip";

/* Constants */
import { basicStyles } from "src/shared/styles/Charts";
import {
  NOT_LOOKED_FOR_LABEL,
  LOOKED_FOR_NO_VALUE_LABEL,
} from "src/shared/constants/charts/NoValueStates";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import { HistogramProps } from "src/shared/interfaces/charts/Histogram";
import {
  ChartTooltipRef,
  StackedCharts,
} from "src/shared/interfaces/ChartTooltip";

/**
 * Append "Not looked for" / "Looked for, no value" as extra discrete bins
 * after the real (continuous, binned) categories, so they render as
 * additional stacked bars alongside the histogram, using the same
 * bar-positioning (`scaleBand`) machinery as the real bins. A bin is only
 * added when at least one series has a strictly positive count for it.
 */
export const appendNoValueBins = (
  formattedDataForStack: Record<string, number>[],
  categories: string[],
  data: HistogramProps[]
): {
  formattedDataForStack: Record<string, number>[];
  categories: string[];
} => {
  const notLookedForRow: Record<string, number> = {};
  const lookedForNoValueRow: Record<string, number> = {};
  let hasNotLookedFor = false;
  let hasLookedForNoValue = false;

  data.forEach((serie) => {
    const name = serie.name ?? "unnamed";
    const notLookedForCount = serie.notLookedForCount ?? 0;
    const lookedForNoValueCount = serie.lookedForNoValueCount ?? 0;
    notLookedForRow[name] = notLookedForCount;
    lookedForNoValueRow[name] = lookedForNoValueCount;
    hasNotLookedFor = hasNotLookedFor || notLookedForCount > 0;
    hasLookedForNoValue =
      hasLookedForNoValue || lookedForNoValueCount > 0;
  });

  // Ordered by information quality: "looked for, no value" (assessed, none
  // found) before "not looked for" (never assessed).
  const extraRows: Record<string, number>[] = [];
  const extraCategories: string[] = [];
  if (hasLookedForNoValue) {
    extraRows.push(lookedForNoValueRow);
    extraCategories.push(LOOKED_FOR_NO_VALUE_LABEL);
  }
  if (hasNotLookedFor) {
    extraRows.push(notLookedForRow);
    extraCategories.push(NOT_LOOKED_FOR_LABEL);
  }

  return {
    formattedDataForStack: [...formattedDataForStack, ...extraRows],
    categories: [...categories, ...extraCategories],
  };
};

/**
 * Draw x-axis-style labels under the two appended no-value bins. They live
 * outside the continuous numeric domain the main x-axis is drawn from, so
 * they don't get labelled by `drawAxes`'s `d3.axisBottom(xScale)` call.
 */
/**
 * Split a wide categorical label into up to two roughly-balanced lines at word
 * boundaries. The no-value labels ("Looked for, no value" / "Not looked for")
 * are far wider than their narrow bars, so a single line overflows into the
 * neighbouring gap and collides with the numeric bin-edge ticks (e.g. "2.0").
 * Single-word labels are returned unchanged.
 */
export const splitLabelIntoLines = (label: string): string[] => {
  const words = label.split(" ");
  if (words.length <= 1) return [label];

  // Pick the word boundary closest to the label's mid-point so both lines end
  // up a similar width.
  const mid = label.length / 2;
  let bestSplit = 1;
  let bestDiff = Infinity;
  let cumulative = 0;
  for (let index = 0; index < words.length - 1; index += 1) {
    cumulative += words[index].length + 1; // +1 for the joining space
    const diff = Math.abs(cumulative - mid);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestSplit = index + 1;
    }
  }

  return [
    words.slice(0, bestSplit).join(" "),
    words.slice(bestSplit).join(" "),
  ];
};

export const drawNoValueBinLabels = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  xBand: d3.ScaleBand<string>,
  plotHeight: number,
  labels: string[]
) => {
  labels.forEach((label) => {
    const x = (xBand(label) ?? 0) + xBand.bandwidth() / 2;

    const text = g
      .append("text")
      .attr("x", x)
      .attr("y", plotHeight + 16)
      .attr("text-anchor", "middle")
      .style("font-size", basicStyles.font.size)
      .style("fill", basicStyles.font.color);

    // Wrap onto (at most) two lines so a wide label doesn't overflow its bar
    // into the neighbouring numeric bin-edge tick.
    splitLabelIntoLines(label).forEach((line, index) => {
      text
        .append("tspan")
        .attr("x", x)
        .attr("dy", index === 0 ? 0 : "1.1em")
        .text(line);
    });
  });
};

export const createGlobalBins = (
  xValues: number[],
  xDomain: [number, number],
  thresholds = 10
): {
  binGenerator: (
    data: number[]
  ) => d3.Bin<number, number>[];
  bins: d3.Bin<number, number>[];
  categories: string[];
} => {
  const binGenerator = d3
    .bin()
    .domain(xDomain)
    .thresholds(thresholds);

  const bins = binGenerator(xValues);
  // d3 bins are half-open `[x0, x1)` except the last one, which is closed
  // `[x0, x1]` so the maximum value falls into it - reflect that in the label.
  const categories = bins.map((bin, index) =>
    index === bins.length - 1
      ? `[${bin.x0}, ${bin.x1}]`
      : `[${bin.x0}, ${bin.x1})`
  );

  return { binGenerator, bins, categories };
};

export const computeHistogramCountsPerBin = (
  data: HistogramProps[],
  binGenerator: (
    data: number[]
  ) => d3.Bin<number, number>[],
  binCount: number
): Record<string, number>[] => {
  return Array.from({ length: binCount }, (_, index) => {
    const entry: Record<string, number> = {};
    data.forEach((serie) => {
      const serieBins = binGenerator(serie.x ?? []);
      entry[serie.name ?? "unnamed"] =
        serieBins[index]?.length ?? 0;
    });
    return entry;
  });
};

export const createXScale = (
  allXValues: number[],
  plotWidth: number
) => {
  return d3
    .scaleLinear()
    .domain(d3.extent(allXValues) as [number, number])
    .nice()
    .range([0, plotWidth]);
};

export const createYScale = (
  formattedDataForStack: Record<string, number>[],
  subgroups: string[],
  plotHeight: number
) => {
  const maxStackValue =
    d3.max(formattedDataForStack, (d) =>
      subgroups.reduce((sum, key) => sum + (d[key] ?? 0), 0)
    ) ?? 0;

  return d3
    .scaleLinear()
    .domain([0, maxStackValue])
    .nice()
    .range([plotHeight, 0]);
};

export const sanitizeGradientId = (label: string) =>
  label.replace(/[^a-zA-Z0-9-_]/g, "-");

export const createGradients = (
  svg: SVGSelection,
  subgroups: string[]
) => {
  const defs = svg.append("defs");

  getStackGradientTargets(subgroups).forEach((subgroup, index) => {
    const colorBase = getCohortGroupColor(subgroup, index);
    const gradientId = `gradient-${sanitizeGradientId(
      subgroup
    )}`;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.brighter(0.3)?.toString() ??
          colorBase
      );

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.darker(0.5)?.toString() ??
          colorBase
      );
  });
};

export const drawAxes = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  xBand: d3.ScaleBand<string>,
  bins: d3.Bin<number, number>[],
  binCategories: string[],
  yScale: d3.ScaleLinear<number, number>,
  plotHeight: number,
  plotWidth: number,
  unit?: string
) => {
  // x-Axis
  // Ticks are drawn at the numeric bin boundaries and positioned from the
  // *band* scale (the same scale that positions the bars) rather than from a
  // continuous linear axis. This keeps ticks aligned with the bars even when
  // extra discrete "no value" bins are appended (which shifts the band layout),
  // and labels come straight from the bin edges (`bin.x0`/`bin.x1`) so they
  // stay correct on non-"nice" domains where axis ticks and bin edges would
  // otherwise be computed independently and drift apart.
  //
  // With `padding(0.1)` the outer margins and inter-bar gaps are equal, so
  // every boundary sits at the midpoint of a gap: half a gap left of each
  // bar, and half a gap right of the last bar.
  const gap = xBand.step() - xBand.bandwidth();
  const formatTick = xScale.tickFormat(bins.length);

  const boundaries = bins.map((bin, index) => ({
    x: (xBand(binCategories[index]) ?? 0) - gap / 2,
    value: bin.x0 ?? 0,
  }));

  const lastBin = bins[bins.length - 1];
  const lastCategory = binCategories[binCategories.length - 1];
  if (lastBin) {
    boundaries.push({
      x:
        (xBand(lastCategory) ?? 0) +
        xBand.bandwidth() +
        gap / 2,
      value: lastBin.x1 ?? 0,
    });
  }

  const xAxis = g
    .append("g")
    .attr("transform", `translate(0,${plotHeight})`);

  xAxis
    .selectAll("text")
    .data(boundaries)
    .enter()
    .append("text")
    .attr("x", (d) => d.x)
    .attr("y", 16)
    .attr("text-anchor", "middle")
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color)
    .text((d) => formatTick(d.value));

  // y-Axis
  const yAxis = g
    .append("g")
    .call(
      d3
        .axisLeft(yScale)
        .ticks(5)
        .tickSize(-plotWidth)
        .tickPadding(21)
    )
    .call((g) => g.select(".domain").remove());

  yAxis
    .selectAll("text")
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  yAxis
    .selectAll(".tick line")
    .attr("stroke", (d) =>
      d === 0
        ? basicStyles.grid.xAxis.zeroLineColor
        : "#ccc"
    )
    .attr("stroke-width", (d) => (d === 0 ? 1.5 : 1))
    .attr("stroke-dasharray", (d) =>
      d === 0 ? "none" : "3,5"
    );

  // x-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("x", plotWidth / 2)
    .attr("y", plotHeight + 62)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color)
    .text(unit ?? "Unit");

  // y-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("transform", "rotate(-90)")
    .attr("x", -plotHeight / 2)
    .attr("y", -62)
    .style("font-size", basicStyles.fontSize.unit)
    .style("fill", basicStyles.font.color)
    .text("Number of patients");
};

const setTooltipData = (tooltipData: StackedCharts) => {
  if (!tooltipData) return [];

  const { value, group } = tooltipData;

  return [
    {
      color: tooltipData.color,
      value: capitalizeFirstLetter(tooltipData.label),
    },
    { key: "Group", value: group },
    { key: "Value", value },
  ];
};

const createPathForRoundedCorners = (
  isTop: boolean,
  x: number,
  y: number,
  height: number,
  width: number,
  radius: number
) => {
  if (isTop && height >= radius) {
    return `
        M${x},${y + height} 
        v${-height + radius} 
        a${radius},${radius} 0 0 1 ${radius},${-radius}
        h${width - 2 * radius} 
        a${radius},${radius} 0 0 1 ${radius},${radius}
        v${height - radius} 
        h${-width} 
        z
    `;
  }

  return `
        M${x},${y + height} 
        v${-height} 
        h${width} 
        v${height} 
        z
    `;
};

export const drawBarsWithRoundedCorners = (
  svg: SVGSelection,
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  stack: d3.Series<Record<string, number>, string>[],
  subgroups: string[],
  categories: string[],
  xBand: any,
  yScale: d3.ScaleLinear<number, number>,
  tooltipRef: RefObject<ChartTooltipRef>
) => {
  const layers = g
    .selectAll(".layer")
    .data(stack)
    .enter()
    .append("g")
    .attr("class", "layer");

  layers
    .selectAll("path")
    .data((layer, layerIndex) =>
      layer.map((point, categoryIndex) => {
        const label = subgroups[layerIndex];
        const category = categories[categoryIndex];

        const isTop =
          layerIndex === stack.length - 1 ||
          stack
            .slice(layer.index + 1)
            .every(
              (upperLayer) =>
                upperLayer[categoryIndex][1] ===
                upperLayer[categoryIndex][0]
            );

        return {
          ...point,
          label,
          category,
          value: point[1] - point[0],
          gradientId: `gradient-${sanitizeGradientId(
            getStackSegmentColorKey(
              label,
              category,
              subgroups
            )
          )}`,
          isTop,
        };
      })
    )
    .enter()
    .append("path")
    .attr("fill", (d) => `url(#${d.gradientId})`)
    .attr("d", (d) => {
      const x = xBand(d.category) ?? 0;
      const y = yScale(d[1]) ?? 0;
      const width = xBand.bandwidth();

      /**
       * Set negative offset for bars in histogram
       * to start at the bottom of zero line.
       */
      const ZERO_LINE_OFFSET = -1.2;

      const yBottomRaw = yScale(d[0]) ?? 0;
      const yBottom =
        d[0] === 0
          ? yBottomRaw - ZERO_LINE_OFFSET
          : yBottomRaw;

      const height = Math.max(0, yBottom - y);
      const radius = 8;

      return createPathForRoundedCorners(
        d.isTop,
        x,
        y,
        height,
        width,
        radius
      );
    })
    .on("mousemove", (event: MouseEvent, d) => {
      const target = event.currentTarget as SVGElement;
      d3.select(target).attr("opacity", 0.85);

      const tooltipData: StackedCharts = {
        label: d.label,
        group: d.category,
        value: d.value,
        color:
          d3
            .color(
              getStackSegmentColor(
                d.label,
                d.category,
                subgroups
              )
            )
            ?.toString() ?? "#000",
      };

      const scssProps: ScssProps = {
        margin: -52,
      };

      createChartTooltip(
        tooltipRef,
        () => setTooltipData(tooltipData),
        svg,
        event,
        scssProps
      );
    })
    .on("mouseleave", (event: MouseEvent) => {
      const target = event.currentTarget as SVGElement;
      d3.select(target).attr("opacity", 1);
      tooltipRef.current?.hideTooltip();
    });
};
